/**
 * A per-business synchronization lock that prevents overlapping sync runs.
 *
 * WHY THIS IS A SEPARATE MODULE
 * -----------------------------
 * The SyncCoordinator (background) and the user's "Sync Now" click can both
 * call `engine.run()` for the same business. Without a lock, both calls
 * call `takePending()` concurrently, get the same batch, and both push the
 * same changes — the second push finding them already `acked` and reporting
 * `0 sent, 0 applied` even though the changes were sent (by the background
 * run). This looks like a bug to the user and obscures the real outcome.
 *
 * The lock is keyed by businessId so different businesses can sync
 * concurrently. The lock is in-memory only (lost on page reload); a crash
 * between two runs is impossible because only one run is ever in flight per
 * business.
 */

type ReleaseFn = () => void;

interface LockState {
  held: boolean;
  waiters: ReleaseFn[];
}

const locks = new Map<string, LockState>();

/**
 * Acquire the sync lock for a business. Blocks until the lock is available.
 *
 * The lock is released by calling the returned function. Every acquire MUST
 * be paired with a release, even on error — use `try/finally`.
 */
export function acquireSyncLock(businessId: string): Promise<ReleaseFn> {
  const existing = locks.get(businessId);
  if (!existing || existing.held === false) {
    if (!existing) {
      locks.set(businessId, { held: true, waiters: [] });
    } else {
      existing.held = true;
    }
    return Promise.resolve(makeRelease(businessId));
  }

  // Lock is held: queue a waiter.
  return new Promise<ReleaseFn>((resolve) => {
    if (!existing) {
      // Lock was released between the check and now.
      locks.set(businessId, { held: true, waiters: [] });
      resolve(makeRelease(businessId));
      return;
    }
    existing.waiters.push(() => resolve(makeRelease(businessId)));
  });
}

function makeRelease(businessId: string): ReleaseFn {
  return () => {
    const state = locks.get(businessId);
    if (!state) return; // already cleaned up
    if (state.waiters.length > 0) {
      const next = state.waiters.shift();
      if (next) next();
    } else {
      state.held = false;
    }
  };
}

/** Reset all locks. For testing only. */
export function resetSyncLock(businessId?: string): void {
  if (businessId) {
    locks.delete(businessId);
  } else {
    locks.clear();
  }
}

/** Check whether a sync run is currently in progress for the given business. */
export function isSyncInProgress(businessId: string): boolean {
  const state = locks.get(businessId);
  return state ? state.held : false;
}
